import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MediaResource } from '@brightmls/nextgen.lae.flc';
import {
  EditState,
  CropPosition,
  CropSize,
  MobileInputType,
  CropModeType,
  CarouselDragState,
  ProcessedImageData,
  BatchProcessedImageData,
} from './types';
import {
  initializeCanvasWithEdit,
  getProcessedImageData,
  getVisibleCarouselImages,
  transformDragDeltaForRotation,
} from './utils';
import {
  DEFAULT_BRIGHTNESS,
  DEFAULT_CONTRAST,
  DEFAULT_SATURATION,
  DEFAULT_ROTATION,
  FOUR_THREE_RATIO,
  CAROUSEL_ITEM_TOTAL_WIDTH,
  BAKED_JPEG_QUALITY,
  CROP_OVERLAY_OPACITY,
  CROP_BORDER_COLOR,
  CROP_BORDER_WIDTH,
  DRAG_HANDLE_SIZE,
  DRAG_HANDLE_OFFSET,
} from './constants';

export interface UseImageEditDialogParams {
  photoMedia: MediaResource[];
  selectedMediaRecord: MediaResource;
}

export interface UseImageEditDialogReturn {
  // State
  selectedMobileInput: MobileInputType;
  setSelectedMobileInput: (input: MobileInputType) => void;
  current: number;
  setCurrent: (index: number) => void;
  editStates: EditState[];
  currentPhoto: MediaResource;
  hasEdits: boolean;
  // Crop states
  isCropping: boolean;
  cropStart: CropPosition | null;
  cropEnd: CropPosition | null;
  isSquareCropMode: boolean;
  squareCropPosition: CropPosition;
  squareSize: number;
  isDraggingSquare: boolean;
  isFourThreeCropMode: boolean;
  fourThreePosition: CropPosition;
  fourThreeSize: CropSize;
  isDraggingFourThree: boolean;
  isDrawing: boolean;

  // Carousel states
  isCarouselDragging: boolean;
  carouselDragOffset: number;
  carouselViewportIndex: number;

  // Refs
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  imageRef: React.RefObject<HTMLImageElement | null>;
  carouselRef: React.RefObject<HTMLDivElement | null>;

  // Actions
  updateCurrentEdit: (newPartialState: Partial<EditState>) => void;
  handlePrev: () => void;
  handleNext: () => void;
  handleReset: () => void;

  // Crop actions
  startManualCrop: () => void;
  startSquareCrop: () => void;
  startFourThreeCrop: () => void;
  applyCrop: () => void;
  applySquareCrop: () => void;
  applyFourThreeModeCrop: () => void;
  cancelSquareCrop: () => void;
  cancelFourThreeCrop: () => void;
  cancelManualCrop: () => void;

  // Event handlers
  handleImageLoad: () => void;
  handleStartCrop: (
    event:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => void;
  handleCropMove: (
    event:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => void;
  handleEndCrop: (
    event:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => void;
  handleSquareDragStart: (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => void;
  handleSquareDragMove: (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => void;
  handleSquareDragEnd: () => void;
  handleFourThreeDragStart: (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => void;
  handleFourThreeDragMove: (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => void;
  handleFourThreeDragEnd: () => void;

  // Carousel handlers
  handleCarouselDragStart: (e: React.MouseEvent | React.TouchEvent) => void;
  handleCarouselDragMove: (e: React.MouseEvent | React.TouchEvent) => void;
  handleCarouselDragEnd: () => void;
  getVisibleCarouselImagesForState: () => any[];

  // Processing
  getAllProcessedImages: () => Promise<BatchProcessedImageData>;
}

export const useImageEditDialog = ({
  photoMedia,
  selectedMediaRecord,
}: UseImageEditDialogParams): UseImageEditDialogReturn => {
  // Find the index of the photo that matches selectedMediaRecord.MED_VW_KEY
  const initialCurrentIndex = photoMedia.findIndex(
    (photo) => photo.MED_VW_KEY === selectedMediaRecord?.MED_VW_KEY,
  );

  // Basic state
  const [selectedMobileInput, setSelectedMobileInput] =
    useState<MobileInputType>('rotate');
  const [current, setCurrent] = useState(initialCurrentIndex);

  // Crop state
  const [isCropping, setIsCropping] = useState(false);
  const [cropStart, setCropStart] = useState<CropPosition | null>(null);
  const [cropEnd, setCropEnd] = useState<CropPosition | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Edit states for all photos
  const [editStates, setEditStates] = useState(
    photoMedia.map(() => ({
      brightness: DEFAULT_BRIGHTNESS,
      contrast: DEFAULT_CONTRAST,
      saturation: DEFAULT_SATURATION,
      rotation: DEFAULT_ROTATION,
      crop: null,
      isSquare: false,
      sourceOverride: null as string | null,
      appliedCrop: null as null | 'square' | 'fourThree' | 'manual',
    })),
  );

  // Square crop states
  const [isSquareCropMode, setIsSquareCropMode] = useState(false);
  const [squareCropPosition, setSquareCropPosition] = useState<CropPosition>({
    x: 0,
    y: 0,
  });
  const [squareSize, setSquareSize] = useState(0);
  const [isDraggingSquare, setIsDraggingSquare] = useState(false);
  const [dragStart, setDragStart] = useState<CropPosition>({ x: 0, y: 0 });

  // 4:3 crop states
  const [isFourThreeCropMode, setIsFourThreeCropMode] = useState(false);
  const [fourThreePosition, setFourThreePosition] = useState<CropPosition>({
    x: 0,
    y: 0,
  });
  const [fourThreeSize, setFourThreeSize] = useState<CropSize>({
    width: 0,
    height: 0,
  });
  const [isDraggingFourThree, setIsDraggingFourThree] = useState(false);
  const [dragStartFourThree, setDragStartFourThree] = useState<CropPosition>({
    x: 0,
    y: 0,
  });

  // Deferred crop mode init
  const [pendingCropMode, setPendingCropMode] = useState<CropModeType>(null);

  // Carousel drag states
  const [isCarouselDragging, setIsCarouselDragging] = useState(false);
  const [carouselDragStart, setCarouselDragStart] = useState<CarouselDragState>(
    { x: 0, viewportIndex: 0 },
  );
  const [carouselDragOffset, setCarouselDragOffset] = useState(0);
  const [carouselViewportIndex, setCarouselViewportIndex] =
    useState(initialCurrentIndex);

  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const currentPhoto = photoMedia[current];

  // Check if current photo has any edits
  const hasEdits = React.useMemo(() => {
    const currentState = editStates[current];
    if (!currentState) return false;

    return (
      currentState.brightness !== DEFAULT_BRIGHTNESS ||
      currentState.contrast !== DEFAULT_CONTRAST ||
      currentState.saturation !== DEFAULT_SATURATION ||
      currentState.rotation !== DEFAULT_ROTATION ||
      currentState.appliedCrop !== null ||
      currentState.sourceOverride !== null ||
      isCropping ||
      isSquareCropMode ||
      isFourThreeCropMode
    );
  }, [editStates, current, isCropping, isSquareCropMode, isFourThreeCropMode]);

  // Update current edit state
  const updateCurrentEdit = useCallback(
    (newPartialState: Partial<EditState>) => {
      setEditStates((states) => {
        const next = states.slice();
        next[current] = { ...next[current], ...newPartialState };
        return next;
      });
    },
    [current, editStates],
  );

  // Get crop coordinates from mouse or touch event
  const getCropCoordinates = useCallback(
    (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const eventTarget = e.target as HTMLElement;
      const parentElement = eventTarget.parentElement as HTMLDivElement;

      const containerBox = parentElement.getBoundingClientRect();
      const canvasBox = canvas.getBoundingClientRect();
      const scaleHeight = containerBox.height / canvas.height;
      const scaleWidth = containerBox.width / canvas.width;
      const scale = Math.min(scaleHeight, scaleWidth);
      const virtualWidth = (canvas.width * scale) / scaleWidth;
      const virtualHeight = (canvas.height * scale) / scaleHeight;
      const clientX =
        'touches' in e
          ? e.touches[0]?.clientX || e.changedTouches[0]?.clientX
          : e.clientX;
      const clientY =
        'touches' in e
          ? e.touches[0]?.clientY || e.changedTouches[0]?.clientY
          : e.clientY;

      const mouseX =
        ((clientX - canvasBox.left) * canvas.width) / canvas.offsetWidth;
      const mouseY =
        ((clientY - canvasBox.top) * canvas.height) / canvas.offsetHeight;
      const x =
        ((mouseX - canvas.width / 2 + virtualWidth / 2) / virtualWidth) *
        canvas.width;
      const y =
        ((mouseY - canvas.height / 2 + virtualHeight / 2) / virtualHeight) *
        canvas.height;

      return {
        x,
        y,
      };
    },
    [canvasRef],
  );

  const drawImageOnCanvas = useCallback(
    (opts?: { showCropBorder: boolean }) => {
      const { showCropBorder = true } = opts || {};
      const canvas = canvasRef.current;
      const img = imageRef.current;

      if (!canvas || !img?.complete) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Clear any existing transformations
      ctx.resetTransform();

      // Initialize main canvas with proper cropping and current edit state
      initializeCanvasWithEdit(canvas, img, editStates[current]);

      const isSquareCrop = isSquareCropMode && squareSize > 0;
      const isFourThreeCrop =
        isFourThreeCropMode &&
        fourThreeSize.width > 0 &&
        fourThreeSize.height > 0;

      // Add square crop overlay if in square crop mode
      if (isSquareCrop || isFourThreeCrop) {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const scale = 1;

        // Calculate display dimensions
        const displayedWidth = img.naturalWidth * scale;
        const displayedHeight = img.naturalHeight * scale;
        const offsetX = (canvas.width - displayedWidth) / 2;
        const offsetY = (canvas.height - displayedHeight) / 2;

        // Save the current transformation matrix
        ctx.save();

        const edit = editStates[current];
        const isVerticalRotation = [90, 270].includes(Math.abs(edit.rotation));
        if (edit.rotation !== 0 && isVerticalRotation) {
          canvas.width = img.naturalHeight;
          canvas.height = img.naturalWidth;
        }

        const width = img.naturalWidth;
        const height = img.naturalHeight;
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((edit.rotation * Math.PI) / 180);
        ctx.drawImage(img, -width / 2, -height / 2, width, height);
        ctx.translate((-1 * canvas.width) / 2, (-1 * canvas.height) / 2);

        const xPos = isSquareCropMode
          ? squareCropPosition.x
          : fourThreePosition.x;
        const yPos = isSquareCropMode
          ? squareCropPosition.y
          : fourThreePosition.y;

        // Calculate square position and size on display
        const x = offsetX + xPos * scale;
        const y = offsetY + yPos * scale;
        // For square mode, w and h are equal
        const cropWidth = isSquareCropMode ? squareSize : fourThreeSize.width;
        const cropHeight = isSquareCropMode ? squareSize : fourThreeSize.height;
        const w = cropWidth * scale;
        const h = cropHeight * scale;

        ctx.translate(offsetX, offsetY);
        // Draw semi-transparent overlay
        ctx.fillStyle = `rgba(0, 0, 0, ${CROP_OVERLAY_OPACITY})`;
        ctx.fillRect(
          0,
          0,
          isVerticalRotation ? canvas.height : canvas.width,
          isVerticalRotation ? canvas.width : canvas.height,
        );
        ctx.translate(-offsetX, -offsetY);

        // Clear the square crop area
        ctx.clearRect(x, y, w, h);

        // Redraw the image in the crop area
        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = w;
        cropCanvas.height = h;
        const cropCtx = cropCanvas.getContext('2d');
        if (cropCtx) {
          cropCtx.filter = `brightness(${editStates[current].brightness}%) contrast(${editStates[current].contrast}%) saturate(${editStates[current].saturation}%)`;

          cropCtx.drawImage(img, xPos, yPos, cropWidth, cropHeight, 0, 0, w, h);

          ctx.drawImage(cropCanvas, x, y);
        }

        // Draw border around square
        ctx.strokeStyle = CROP_BORDER_COLOR;
        ctx.lineWidth = CROP_BORDER_WIDTH;
        ctx.strokeRect(x, y, w, h);

        // Add drag handle in center
        ctx.fillStyle = CROP_BORDER_COLOR;
        ctx.fillRect(
          x + w / 2 - DRAG_HANDLE_OFFSET,
          y + h / 2 - DRAG_HANDLE_OFFSET,
          DRAG_HANDLE_SIZE,
          DRAG_HANDLE_SIZE,
        );

        // Restore the transformation matrix
        ctx.restore();
      }
      // Add crop overlay if in manual cropping mode
      else if (isCropping) {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // If we have crop selection, show the selection
        if (cropStart && cropEnd) {
          // Create a temporary canvas to store the current image content
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = canvas.width;
          tempCanvas.height = canvas.height;
          const tempCtx = tempCanvas.getContext('2d');
          if (!tempCtx) return;

          // Copy current canvas content (including rotation) to temp canvas
          tempCtx.drawImage(canvas, 0, 0);

          // Now reset transformations on main canvas for overlay operations
          ctx.resetTransform();

          // Draw semi-transparent overlay in screen coordinates
          ctx.fillStyle = `rgba(0, 0, 0, ${CROP_OVERLAY_OPACITY})`;
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Calculate crop dimensions in screen coordinates
          const x = Math.min(cropStart.x, cropEnd.x);
          const y = Math.min(cropStart.y, cropEnd.y);
          const width = Math.abs(cropEnd.x - cropStart.x);
          const height = Math.abs(cropEnd.y - cropStart.y);

          if (showCropBorder) {
            // Draw crop border in screen coordinates
            ctx.strokeStyle = CROP_BORDER_COLOR;
            ctx.lineWidth = CROP_BORDER_WIDTH;
            ctx.strokeRect(x, y, width, height);
          }

          // Restore the rotated image content in the crop area
          ctx.drawImage(tempCanvas, x, y, width, height, x, y, width, height);
        } else {
          // Show darkened overlay when in crop mode but no selection yet
          ctx.resetTransform();
          ctx.fillStyle = `rgba(0, 0, 0, ${CROP_OVERLAY_OPACITY})`;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      }
    },
    [
      editStates,
      current,
      isSquareCropMode,
      squareCropPosition,
      squareSize,
      isFourThreeCropMode,
      fourThreePosition,
      fourThreeSize,
      isCropping,
      cropStart,
      cropEnd,
    ],
  );

  // Navigation handlers
  const handlePrev = useCallback(() => {
    setCurrent((prev) => (prev > 0 ? prev - 1 : photoMedia.length - 1));
  }, [photoMedia.length]);

  const handleNext = useCallback(() => {
    setCurrent((prev) => (prev < photoMedia.length - 1 ? prev + 1 : 0));
  }, [photoMedia.length]);

  // Reset handler
  const handleReset = useCallback(() => {
    updateCurrentEdit({
      brightness: DEFAULT_BRIGHTNESS,
      contrast: DEFAULT_CONTRAST,
      saturation: DEFAULT_SATURATION,
      rotation: DEFAULT_ROTATION,
      crop: null,
      isSquare: false,
      sourceOverride: null,
      appliedCrop: null,
    });
    setIsSquareCropMode(false);
    setIsFourThreeCropMode(false);
    setIsCropping(false);
    setCropStart(null);
    setCropEnd(null);
  }, [updateCurrentEdit]);

  // Crop handlers
  const startManualCrop = useCallback(() => {
    // Clear all crop-related states to prevent mismatched boxes
    setIsSquareCropMode(false);
    setIsFourThreeCropMode(false);
    setIsCropping(true);
    setCropStart(null);
    setCropEnd(null);
    setIsDrawing(false);

    // Ensure canvas is redrawn after state changes
    setTimeout(() => drawImageOnCanvas(), 0);
  }, [drawImageOnCanvas]);

  const startSquareCrop = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;

    const minSide = Math.min(img.naturalWidth, img.naturalHeight);
    const initialX = (img.naturalWidth - minSide) / 2;
    const initialY = (img.naturalHeight - minSide) / 2;

    setIsCropping(false);
    setCropStart(null);
    setCropEnd(null);
    setIsDrawing(false);
    setIsFourThreeCropMode(false);
    setSquareSize(minSide);
    setSquareCropPosition({ x: initialX, y: initialY });
    setIsSquareCropMode(true);
  }, [editStates, current]);

  const startFourThreeCrop = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;

    let width = img.naturalWidth;
    let height = Math.round(width / FOUR_THREE_RATIO);
    if (height > img.naturalHeight) {
      height = img.naturalHeight;
      width = Math.round(height * FOUR_THREE_RATIO);
    }
    const initialX = (img.naturalWidth - width) / 2;
    const initialY = (img.naturalHeight - height) / 2;

    setIsCropping(false);
    setCropStart(null);
    setCropEnd(null);
    setIsDrawing(false);
    setIsSquareCropMode(false);
    setFourThreeSize({ width, height });
    setFourThreePosition({ x: initialX, y: initialY });
    setIsFourThreeCropMode(true);
  }, [editStates, current]);

  // Apply crop functions
  const applyCrop = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;

    // If no valid crop selection, reset crop mode instead of doing nothing
    if (!canvas || !img || !cropStart || !cropEnd) {
      // Reset crop state to avoid mismatched UI state
      setCropStart(null);
      setCropEnd(null);
      setIsCropping(false);
      setIsDrawing(false);
      drawImageOnCanvas();
      return;
    }

    // Calculate crop dimensions
    const x = Math.min(cropStart.x, cropEnd.x);
    const y = Math.min(cropStart.y, cropEnd.y);
    const width = Math.abs(cropEnd.x - cropStart.x);
    const height = Math.abs(cropEnd.y - cropStart.y);

    // Ensure we have a meaningful crop area (at least 10x10 pixels)
    if (width < 10 || height < 10) {
      // Reset crop state for too small selections
      setCropStart(null);
      setCropEnd(null);
      setIsCropping(false);
      setIsDrawing(false);
      drawImageOnCanvas();
      return;
    }

    // Create a new canvas for the cropped area
    const newCanvas = document.createElement('canvas');
    newCanvas.width = width;
    newCanvas.height = height;
    const newCtx = newCanvas.getContext('2d');
    if (!newCtx) return;

    newCtx.filter = `brightness(${editStates[current].brightness}%) contrast(${editStates[current].contrast}%) saturate(${editStates[current].saturation}%)`;
    // Draw the cropped portion from the CURRENT canvas content
    newCtx.drawImage(canvas, x, y, width, height, 0, 0, width, height);

    const bakedUrl = newCanvas.toDataURL('image/jpeg', BAKED_JPEG_QUALITY);
    setEditStates((states) => {
      const next = states.slice();
      next[current] = {
        ...next[current],
        sourceOverride: bakedUrl,
        rotation: 0,
        isSquare: false,
        appliedCrop: 'manual',
      };
      return next;
    });

    setCropStart(null);
    setCropEnd(null);
    setIsCropping(false);
    setIsDrawing(false);
    setTimeout(() => drawImageOnCanvas({ showCropBorder: false }));
  }, [editStates, cropStart, cropEnd, current, drawImageOnCanvas]);

  const applySquareCrop = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img || !isSquareCropMode) return;

    const newCanvas = document.createElement('canvas');
    newCanvas.width = squareSize;
    newCanvas.height = squareSize;
    const newCtx = newCanvas.getContext('2d');
    if (!newCtx) return;

    newCtx.filter = `brightness(${editStates[current].brightness}%) contrast(${editStates[current].contrast}%) saturate(${editStates[current].saturation}%)`;
    newCtx.drawImage(
      img,
      squareCropPosition.x,
      squareCropPosition.y,
      squareSize,
      squareSize,
      0,
      0,
      squareSize,
      squareSize,
    );

    if (editStates[current].rotation !== 0) {
      newCtx.restore();
    }

    const bakedUrl = newCanvas.toDataURL('image/jpeg', BAKED_JPEG_QUALITY);
    setEditStates((states) => {
      const next = states.slice();
      next[current] = {
        ...next[current],
        sourceOverride: bakedUrl,
        // Reset filters to defaults since they're baked into the cropped image
        isSquare: false,
        appliedCrop: 'square',
      };
      return next;
    });

    setIsSquareCropMode(false);
    setTimeout(() => drawImageOnCanvas());
  }, [editStates, current, squareSize, squareCropPosition, drawImageOnCanvas]);

  const applyFourThreeModeCrop = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img || !isFourThreeCropMode) return;

    const { x, y } = fourThreePosition;
    const { width, height } = fourThreeSize;

    const newCanvas = document.createElement('canvas');
    newCanvas.width = width;
    newCanvas.height = height;
    const newCtx = newCanvas.getContext('2d');
    if (!newCtx) return;

    newCtx.filter = `brightness(${editStates[current].brightness}%) contrast(${editStates[current].contrast}%) saturate(${editStates[current].saturation}%)`;
    newCtx.drawImage(img, x, y, width, height, 0, 0, width, height);

    if (editStates[current].rotation !== 0) {
      newCtx.restore();
    }

    const bakedUrl = newCanvas.toDataURL('image/jpeg', BAKED_JPEG_QUALITY);
    setEditStates((states) => {
      const next = states.slice();
      next[current] = {
        ...next[current],
        sourceOverride: bakedUrl,
        isSquare: false,
        appliedCrop: 'fourThree',
      };
      return next;
    });

    setIsFourThreeCropMode(false);
    setTimeout(() => drawImageOnCanvas());
  }, [
    editStates,
    current,
    fourThreePosition,
    fourThreeSize,
    drawImageOnCanvas,
  ]);

  const cancelSquareCrop = useCallback(() => {
    setIsSquareCropMode(false);
    drawImageOnCanvas();
  }, [drawImageOnCanvas]);

  const cancelFourThreeCrop = useCallback(() => {
    setIsFourThreeCropMode(false);
    drawImageOnCanvas();
  }, [drawImageOnCanvas]);

  const cancelManualCrop = useCallback(() => {
    setIsCropping(false);
    setCropStart(null);
    setCropEnd(null);
    setIsDrawing(false);
    drawImageOnCanvas();
  }, [drawImageOnCanvas]);

  // Mouse and touch event handlers
  const handleStartCrop = useCallback(
    (
      event:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      if (!isCropping) return;
      const coords = getCropCoordinates(event);
      setCropStart(coords);
      setCropEnd(coords);
      setIsDrawing(true);
      event.preventDefault(); // Prevent default touch behavior
    },
    [isCropping, getCropCoordinates],
  );

  const handleCropMove = useCallback(
    (
      event:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      if (!isCropping || !isDrawing) return;
      event.preventDefault();
      const coords = getCropCoordinates(event);
      setCropEnd(coords);
      drawImageOnCanvas();
    },
    [isCropping, isDrawing, getCropCoordinates, drawImageOnCanvas],
  );

  const handleEndCrop = useCallback(
    (
      event:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      event.preventDefault();
      setIsDrawing(false);
      if (cropStart && cropEnd) {
        drawImageOnCanvas();
      }
    },
    [cropStart, cropEnd, drawImageOnCanvas],
  );

  // Square crop drag handlers
  const handleSquareDragStart = useCallback(
    (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      if (!isSquareCropMode) return;
      const canvas = canvasRef.current;
      const img = imageRef.current;
      if (!canvas || !img) return;

      const rect = canvas.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const clickX = clientX - rect.left;
      const clickY = clientY - rect.top;

      const scaleX = rect.width / img.naturalWidth;
      const scaleY = rect.height / img.naturalHeight;
      const scale = Math.min(scaleX, scaleY);

      const displayedWidth = img.naturalWidth * scale;
      const displayedHeight = img.naturalHeight * scale;
      const offsetX = (rect.width - displayedWidth) / 2;
      const offsetY = (rect.height - displayedHeight) / 2;

      const squareDisplayX = offsetX + squareCropPosition.x * scale;
      const squareDisplayY = offsetY + squareCropPosition.y * scale;
      const squareDisplaySize = squareSize * scale;

      if (
        clickX >= squareDisplayX &&
        clickX <= squareDisplayX + squareDisplaySize &&
        clickY >= squareDisplayY &&
        clickY <= squareDisplayY + squareDisplaySize
      ) {
        setIsDraggingSquare(true);
        setDragStart({ x: clientX, y: clientY });
        e.preventDefault();
      }
    },
    [isSquareCropMode, squareCropPosition, squareSize],
  );

  const handleSquareDragMove = useCallback(
    (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      if (!isDraggingSquare || !isSquareCropMode) return;

      const canvas = canvasRef.current;
      const img = imageRef.current;
      if (!canvas || !img) return;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const dx = clientX - dragStart.x;
      const dy = clientY - dragStart.y;

      const scaleX = canvas.width / img.naturalWidth;
      const scaleY = canvas.height / img.naturalHeight;
      const scale = Math.min(scaleX, scaleY);

      // Transform drag delta based on current rotation
      const currentRotation = editStates[current].rotation;
      const { dx: transformedDx, dy: transformedDy } =
        transformDragDeltaForRotation(dx, dy, currentRotation);

      const imageDx = transformedDx / scale;
      const imageDy = transformedDy / scale;

      setSquareCropPosition((prev) => {
        const newX = Math.max(
          0,
          Math.min(prev.x + imageDx, img.naturalWidth - squareSize),
        );
        const newY = Math.max(
          0,
          Math.min(prev.y + imageDy, img.naturalHeight - squareSize),
        );
        return { x: newX, y: newY };
      });

      setDragStart({ x: clientX, y: clientY });
      e.preventDefault();
    },
    [
      isDraggingSquare,
      isSquareCropMode,
      dragStart,
      squareSize,
      editStates,
      current,
    ],
  );

  const handleSquareDragEnd = useCallback(() => {
    setIsDraggingSquare(false);
  }, []);

  // 4:3 crop drag handlers
  const handleFourThreeDragStart = useCallback(
    (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      if (!isFourThreeCropMode) return;
      const canvas = canvasRef.current;
      const img = imageRef.current;
      if (!canvas || !img) return;

      const rect = canvas.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const clickX = clientX - rect.left;
      const clickY = clientY - rect.top;

      const scaleX = rect.width / img.naturalWidth;
      const scaleY = rect.height / img.naturalHeight;
      const scale = Math.min(scaleX, scaleY);
      const displayedWidth = img.naturalWidth * scale;
      const displayedHeight = img.naturalHeight * scale;
      const offsetX = (rect.width - displayedWidth) / 2;
      const offsetY = (rect.height - displayedHeight) / 2;

      const rectX = offsetX + fourThreePosition.x * scale;
      const rectY = offsetY + fourThreePosition.y * scale;
      const rectWidth = fourThreeSize.width * scale;
      const rectHeight = fourThreeSize.height * scale;

      if (
        clickX >= rectX &&
        clickX <= rectX + rectWidth &&
        clickY >= rectY &&
        clickY <= rectY + rectHeight
      ) {
        setIsDraggingFourThree(true);
        setDragStartFourThree({ x: clientX, y: clientY });
        e.preventDefault();
      }
    },
    [isFourThreeCropMode, fourThreePosition, fourThreeSize],
  );

  const handleFourThreeDragMove = useCallback(
    (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      if (!isDraggingFourThree || !isFourThreeCropMode) return;

      const canvas = canvasRef.current;
      const img = imageRef.current;
      if (!canvas || !img) return;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const dx = clientX - dragStartFourThree.x;
      const dy = clientY - dragStartFourThree.y;
      const scaleX = canvas.width / img.naturalWidth;
      const scaleY = canvas.height / img.naturalHeight;
      const scale = Math.min(scaleX, scaleY);

      // Transform drag delta based on current rotation
      const currentRotation = editStates[current].rotation;
      const { dx: transformedDx, dy: transformedDy } =
        transformDragDeltaForRotation(dx, dy, currentRotation);

      const imageDx = transformedDx / scale;
      const imageDy = transformedDy / scale;

      setFourThreePosition((prev) => {
        const newX = Math.max(
          0,
          Math.min(prev.x + imageDx, img.naturalWidth - fourThreeSize.width),
        );
        const newY = Math.max(
          0,
          Math.min(prev.y + imageDy, img.naturalHeight - fourThreeSize.height),
        );
        return { x: newX, y: newY };
      });

      setDragStartFourThree({ x: clientX, y: clientY });
      e.preventDefault();
    },
    [
      isDraggingFourThree,
      isFourThreeCropMode,
      dragStartFourThree,
      fourThreeSize,
      editStates,
      current,
    ],
  );

  const handleFourThreeDragEnd = useCallback(() => {
    setIsDraggingFourThree(false);
  }, []);

  // Image load handler
  const handleImageLoad = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;

    if (pendingCropMode === 'square') {
      const minSide = Math.min(img.naturalWidth, img.naturalHeight);
      const initialX = (img.naturalWidth - minSide) / 2;
      const initialY = (img.naturalHeight - minSide) / 2;
      setIsFourThreeCropMode(false);
      setSquareSize(minSide);
      setSquareCropPosition({ x: initialX, y: initialY });
      setIsSquareCropMode(true);
      setPendingCropMode(null);
    } else if (pendingCropMode === 'fourThree') {
      let width = img.naturalWidth;
      let height = Math.round(width / FOUR_THREE_RATIO);
      if (height > img.naturalHeight) {
        height = img.naturalHeight;
        width = Math.round(height * FOUR_THREE_RATIO);
      }
      const initialX = (img.naturalWidth - width) / 2;
      const initialY = (img.naturalHeight - height) / 2;
      setIsSquareCropMode(false);
      setFourThreeSize({ width, height });
      setFourThreePosition({ x: initialX, y: initialY });
      setIsFourThreeCropMode(true);
      setPendingCropMode(null);
    }

    drawImageOnCanvas();
  }, [pendingCropMode, drawImageOnCanvas]);

  // Carousel handlers
  const handleCarouselDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      setIsCarouselDragging(true);
      setCarouselDragStart({
        x: clientX,
        viewportIndex: carouselViewportIndex,
      });
      setCarouselDragOffset(0);
      e.preventDefault();
    },
    [carouselViewportIndex],
  );

  const handleCarouselDragMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isCarouselDragging || !carouselRef.current) return;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const deltaX = clientX - carouselDragStart.x;

      setCarouselDragOffset(deltaX);

      const draggedItems = Math.round(-deltaX / CAROUSEL_ITEM_TOTAL_WIDTH);

      if (draggedItems !== 0) {
        const newViewportIndex = Math.max(
          0,
          Math.min(
            photoMedia.length - 1,
            carouselDragStart.viewportIndex + draggedItems,
          ),
        );

        if (newViewportIndex !== carouselViewportIndex) {
          setCarouselViewportIndex(newViewportIndex);
          setCarouselDragStart({ x: clientX, viewportIndex: newViewportIndex });
          setCarouselDragOffset(0);
        }
      }
    },
    [
      isCarouselDragging,
      carouselDragStart,
      carouselViewportIndex,
      photoMedia.length,
    ],
  );

  const handleCarouselDragEnd = useCallback(() => {
    if (!isCarouselDragging) return;

    const draggedItems = Math.round(
      -carouselDragOffset / CAROUSEL_ITEM_TOTAL_WIDTH,
    );

    if (draggedItems !== 0) {
      const newViewportIndex = Math.max(
        0,
        Math.min(photoMedia.length - 1, carouselViewportIndex + draggedItems),
      );
      setCarouselViewportIndex(newViewportIndex);
    }

    setIsCarouselDragging(false);
    setCarouselDragOffset(0);
  }, [
    isCarouselDragging,
    carouselDragOffset,
    carouselViewportIndex,
    photoMedia.length,
  ]);

  const getVisibleCarouselImagesForState = useCallback(() => {
    return getVisibleCarouselImages(photoMedia, carouselViewportIndex);
  }, [photoMedia, carouselViewportIndex]);

  // Process all images
  const getAllProcessedImages =
    useCallback(async (): Promise<BatchProcessedImageData> => {
      const processedImages: ProcessedImageData[] = [];

      for (let i = 0; i < editStates.length; i++) {
        const processedImage = await getProcessedImageData(
          photoMedia[i],
          editStates[i],
        );
        if (processedImage) {
          processedImages.push(processedImage);
        }
      }

      return {
        processedImages,
        totalProcessed: processedImages.length,
      };
    }, [editStates, photoMedia]);

  // Effects
  useEffect(() => {
    const handleMouseUp = () => {
      if (isDrawing) {
        setIsDrawing(false);
        if (cropStart && cropEnd) {
          drawImageOnCanvas();
        }
      }
    };

    const handleTouchEnd = () => {
      if (isDrawing) {
        setIsDrawing(false);
        if (cropStart && cropEnd) {
          drawImageOnCanvas();
        }
      }
    };

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDrawing, cropStart, cropEnd, drawImageOnCanvas]);

  useEffect(() => {
    drawImageOnCanvas();
  }, [drawImageOnCanvas]);

  // Ensure overlay shows when entering crop mode
  useEffect(() => {
    if (isCropping && !cropStart && !cropEnd) {
      // Small delay to ensure state has fully updated
      setTimeout(() => drawImageOnCanvas(), 10);
    }
  }, [isCropping, cropStart, cropEnd, drawImageOnCanvas]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isCarouselDragging) {
        handleCarouselDragEnd();
      }
    };

    const handleGlobalTouchEnd = () => {
      if (isCarouselDragging) {
        handleCarouselDragEnd();
      }
    };

    if (isCarouselDragging) {
      window.addEventListener('mouseup', handleGlobalMouseUp);
      window.addEventListener('touchend', handleGlobalTouchEnd);
      window.addEventListener('mouseleave', handleGlobalMouseUp);
    }

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalTouchEnd);
      window.removeEventListener('mouseleave', handleGlobalMouseUp);
    };
  }, [isCarouselDragging, handleCarouselDragEnd]);

  useEffect(() => {
    if (!isCarouselDragging) {
      setCarouselViewportIndex(current);
    }
  }, [current, photoMedia.length, isCarouselDragging]);

  return {
    // State
    selectedMobileInput,
    setSelectedMobileInput,
    current,
    setCurrent,
    editStates,
    currentPhoto,

    hasEdits,

    // Crop states
    isCropping,
    cropStart,
    cropEnd,
    isSquareCropMode,
    squareCropPosition,
    squareSize,
    isDraggingSquare,
    isFourThreeCropMode,
    fourThreePosition,
    fourThreeSize,
    isDraggingFourThree,
    isDrawing,

    // Carousel states
    isCarouselDragging,
    carouselDragOffset,
    carouselViewportIndex,

    // Refs
    canvasRef,
    imageRef,
    carouselRef,

    // Actions
    updateCurrentEdit,
    handlePrev,
    handleNext,
    handleReset,

    // Crop actions
    startManualCrop,
    startSquareCrop,
    startFourThreeCrop,
    applyCrop,
    applySquareCrop,
    applyFourThreeModeCrop,
    cancelSquareCrop,
    cancelFourThreeCrop,
    cancelManualCrop,

    // Event handlers
    handleImageLoad,
    handleStartCrop,
    handleCropMove,
    handleEndCrop,
    handleSquareDragStart,
    handleSquareDragMove,
    handleSquareDragEnd,
    handleFourThreeDragStart,
    handleFourThreeDragMove,
    handleFourThreeDragEnd,

    // Carousel handlers
    handleCarouselDragStart,
    handleCarouselDragMove,
    handleCarouselDragEnd,
    getVisibleCarouselImagesForState,

    // Processing
    getAllProcessedImages,
  };
};
